import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../utils';

// Horizontal line separator
const HorizontalLineIcon = () => (
  <div className="w-[18px] h-[1px] bg-muted-foreground" aria-hidden="true" />
);

const StepProgress = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'nav'> & {
    separator?: React.ReactNode;
  }
>(({ ...props }, ref) => (
  <nav ref={ref} aria-label="step progress" {...props} />
));
StepProgress.displayName = 'StepProgress';

const StepProgressList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<'ol'>
>(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      'flex flex-wrap items-center gap-3 break-words text-base text-muted-foreground',
      className,
    )}
    {...props}
  />
));
StepProgressList.displayName = 'StepProgressList';

const StepProgressItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('inline-flex items-center gap-2', className)}
    {...props}
  />
));
StepProgressItem.displayName = 'StepProgressItem';

const stepProgressButtonVariants = cva(
  'transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring p-0 font-medium h-fit hover:underline hover:bg-transparent bg-transparent border-none cursor-pointer flex items-center gap-1.5',
  {
    variants: {
      variant: {
        default: 'text-grey-60 [&>*]:text-muted-foreground',
        active: 'text-foreground',
        disabled:
          'text-muted-foreground [&>*]:text-muted-foreground cursor-not-allowed hover:no-underline pointer-events-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const StepProgressButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'> &
    VariantProps<typeof stepProgressButtonVariants>
>(({ className, variant, disabled, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cn(
        stepProgressButtonVariants({
          variant: disabled ? 'disabled' : variant,
        }),
        className,
      )}
      {...props}
    />
  );
});
StepProgressButton.displayName = 'StepProgressButton';

const stepProgressLinkVariants = cva(
  'transition-colors hover:underline hover:text-text-primary hover:text-foreground flex items-center gap-1.5 font-medium hover:cursor-pointer disabled:cursor-default',
  {
    variants: {
      variant: {
        default: 'text-grey-60 [&>*]:text-muted-foreground',
        active: 'text-foreground [&_svg]:text-foreground',
        disabled:
          'text-muted-foreground [&>*]:text-muted-foreground cursor-not-allowed hover:no-underline pointer-events-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface StepProgressLinkProps
  extends React.ComponentPropsWithoutRef<'a'>,
    VariantProps<typeof stepProgressLinkVariants> {
  disabled?: boolean;
  id: string;
}

const StepProgressLink = React.forwardRef<
  HTMLAnchorElement,
  StepProgressLinkProps
>(({ className, variant, disabled, href, ...props }, ref) => {
  return (
    <a
      ref={ref}
      href={disabled ? undefined : href}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      className={cn(
        stepProgressLinkVariants({
          variant: disabled ? 'disabled' : variant,
        }),
        className,
      )}
      {...props}
    />
  );
});
StepProgressLink.displayName = 'StepProgressLink';

const StepProgressSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn('flex items-center justify-center [&>svg]:size-5', className)}
    {...props}
  >
    {children ?? <HorizontalLineIcon />}
  </li>
);
StepProgressSeparator.displayName = 'StepProgressSeparator';

const StepProgressIcon = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, children, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('flex items-center justify-center [&>svg]:size-6', className)}
    {...props}
  >
    {children}
  </span>
));
StepProgressIcon.displayName = 'StepProgressIcon';

export {
  StepProgress,
  StepProgressList,
  StepProgressItem,
  StepProgressLink,
  StepProgressSeparator,
  StepProgressButton,
  StepProgressIcon,
};
